import {
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  styled,
  Typography
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DownloadIcon from '@material-ui/icons/GetApp';
import GroupIcon from '@material-ui/icons/Group';
import BookIcon from '@material-ui/icons/ImportContacts';
import { Alert } from '@material-ui/lab';
import { useRefreshData } from 'components/main/LessonsProvider';
import { useIsAdminUser } from 'components/user/UserProvider';
import React, { FC } from 'react';
import { deleteLesson, updateLessonApproval } from 'services/LessonService';
import { useLesson } from '../LessonProvider';
import DeleteConfirmation from './components/DeleteConfirmation';
import Details from './components/Details';
import Header from './components/Header';
import OverviewList from './components/OverviewList';

const LessonCard: FC = () => {
  const [expanded, setExpanded] = React.useState(false);
  const [showDelete, setShowDelete] = React.useState(false);
  const isAdminUser = useIsAdminUser();
  const lesson = useLesson();
  const refreshLessonsData = useRefreshData();
  const {
    lessonId,
    lessonTitle: title,
    lessonDescription: description,
    lessonFileId,
    subjectAreas,
    gradeLevels,
    submissionApproved: isApproved,
  } = lesson;

  function handleExpandClick() {
    setExpanded(!expanded);
  }

  function handleApproveClick() {
    updateLessonApproval(lesson, true);
    refreshLessonsData();
  }

  function handleUnapproveClick() {
    updateLessonApproval(lesson, false);
    refreshLessonsData();
  }

  function handleDelete() {
    deleteLesson(lessonId);
    handleDeleteClose();
    refreshLessonsData();
  }

  function handleDeleteClick() {
    setShowDelete(true);
  }

  function handleDeleteClose() {
    setShowDelete(false);
  }

  return (
    <>
      <FixedWidthCard>
        <UnpaddedCardContent>
          <Header>{title}</Header>
          {isAdminUser && !isApproved && (
            <ApprovalGrid justify="space-between" container>
              <Grid item>
                <Typography variant="h5" component="span" color="error">
                  Status: UNAPPROVED
                </Typography>
              </Grid>
              <Grid item>
                <ButtonGrid spacing={1} container>
                  <Grid item>
                    <ApprovalButton variant="contained" onClick={handleApproveClick}>
                      Approve
                    </ApprovalButton>
                  </Grid>
                  <Grid item>
                    <DeleteButton variant="contained" onClick={handleDeleteClick}>
                      Delete
                    </DeleteButton>
                  </Grid>
                </ButtonGrid>
              </Grid>
            </ApprovalGrid>
          )}
          {isAdminUser && isApproved && (
            <ApprovalGrid justify="space-between" container>
              <Grid item>
                <ApprovedTypography variant="h5">Status: APPROVED</ApprovedTypography>
              </Grid>
              <Grid item>
                <ButtonGrid spacing={1} container>
                  <Grid item>
                    <UnapprovalButton variant="contained" onClick={handleUnapproveClick}>
                      Unapprove
                    </UnapprovalButton>
                  </Grid>
                  <Grid item>
                    <DeleteButton variant="contained" onClick={handleDeleteClick}>
                      Delete
                    </DeleteButton>
                  </Grid>
                </ButtonGrid>
              </Grid>
            </ApprovalGrid>
          )}
          {isAdminUser && !lessonFileId && (
            <Alert severity="warning">
              This lesson is missing an associated file. Please contact the submitter and have them
              resubmit this lesson. This lesson can then safely be deleted.
            </Alert>
          )}
          <BodyGrid container>
            <Grid xs={4} item>
              <OverviewList icon={<BookIcon />} title="Subject Area" listItems={subjectAreas} />
              <OverviewList icon={<GroupIcon />} title="Grade Levels" listItems={gradeLevels} />

              <ViewButton
                href={lessonFileId}
                color="primary"
                size="small"
                target="_blank"
                startIcon={<DownloadIcon />}
              >
                View Lesson Resources
              </ViewButton>
            </Grid>
            <DescriptionGrid xs item>
              <Typography variant="body2">{description}</Typography>
            </DescriptionGrid>
          </BodyGrid>
          <Details expanded={expanded} />
          <ExpandButtonContainer maxWidth="md">
            <Button
              color="primary"
              size="small"
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={handleExpandClick}
              aria-expanded={expanded}
            >
              Details
            </Button>
          </ExpandButtonContainer>
        </UnpaddedCardContent>
      </FixedWidthCard>
      <DeleteConfirmation
        name={title}
        open={showDelete}
        onDelete={handleDelete}
        onClose={handleDeleteClose}
      />
    </>
  );
};

// Styled components
const FixedWidthCard = styled(Card)({
  maxWidth: 750,
});

const UnpaddedCardContent = styled(CardContent)({
  padding: 0,
  // Required to override MUI styles
  '&:last-child': {
    paddingBottom: 8,
  },
});

const BodyGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const ExpandButtonContainer = styled(Container)({
  textAlign: 'center',
});

const DescriptionGrid = styled(Grid)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}));

const ViewButton = styled(Button)({
  textTransform: 'none',
  // Increased specifity to override MUI styles
  '&& svg': {
    fontSize: '20px',
  },
  // casting here as documented in https://github.com/mui-org/material-ui/issues/15695#issuecomment-498242520
}) as typeof Button;

const ApprovalGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const ButtonGrid = styled(Grid)({
  margin: 0,
});

const ApprovalButton = styled(Button)(({ theme }) => ({
  color: '#ffffff',
  backgroundColor: theme.palette.success.main,

  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },
}));

const UnapprovalButton = styled(Button)(({ theme }) => ({
  color: '#ffffff',
  backgroundColor: theme.palette.error.main,

  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  color: '#ffffff',
  backgroundColor: theme.palette.error.main,

  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
}));

const ApprovedTypography = styled((props) => <Typography component="span" {...props} />)(
  ({ theme }) => ({
    color: theme.palette.success.main,
  }),
);

export default LessonCard;
